import { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "@/app/auth/auth.slice";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { InputWithCornerHint } from "@/shared/components/atoms/InputWithCornerHint";
import StatsUI from "@/shared/components/molecules/StatsUI";
import { Select } from "@/shared/components/organisms/Select";
import MassiveTaskDialog from "@/shared/components/organisms/MassiveTaskDialog";
import NavbarFilter from "@/shared/components/organisms/NavbarFilter";
import { MultiSelect } from "@/shared/components/organisms/MultiSelect";
import { OptionsMenuProps } from "@/shared/components/molecules/EllipsesMenu";
import { useLazyCatchmentKpiQuery } from "@/app/catchment/catchment.api";
import { StatItemProps } from "@/shared/components/atoms/StatItemUI";
import {
  getOptionsStatusBusiness,
  optionsGender,
} from "@/shared/constants/constants";
import { User } from "@/app/auth/auth.type";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLoader } from "@fortawesome/pro-regular-svg-icons";

export type NavbarFilterCatchmentProps = {
  branches: any[];
  optionsTagsMarketing: any[];
  campaigns: any[];
  exportData: any;
  branchId: string;
  // Prop para recibir la sucursal actualmente seleccionada desde el componente padre.
  selectedBranch: any;
  // Prop para pasar la nueva sucursal seleccionada al componente padre.
  selectBranch: (branch: any) => void;
  // Valores del reducer
  formValues: any;
  dispatch: any;
  selectedCompany: any;
  companies: any;
};

interface GenderOption {
  id: string;
  name: string;
}

const NavbarFilterCatchment: React.FC<NavbarFilterCatchmentProps> = ({
  branches,
  companies,
  optionsTagsMarketing,
  campaigns,
  exportData,
  branchId,
  // Props para manejar el estado de la sucursal de forma controlada.
  selectedBranch,
  selectBranch,
  selectedCompany,
  // Valores del reducer
  formValues,
  dispatch,
}) => {
  const [trigger, { isLoading, isFetching }] = useLazyCatchmentKpiQuery();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [statsUI, setStatsUI] = useState<StatItemProps[]>([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isKpisOpen, setIsKpisOpen] = useState(false);

  const user: User = useSelector(selectUser);

  const optionsMenu: OptionsMenuProps[] = [
    {
      name: "Campañas",
      functionMenu: () => navigate(`/branchs/${branchId}/marketing/campaigns`),
    },
    {
      name: "Tareas Masivas",
      functionMenu: () => setOpen((prev) => !prev),
    },
  ];

  const optionsStatusBusiness = getOptionsStatusBusiness();
  const toggleDrawer = useCallback(() => setIsDrawerOpen((prev) => !prev), []);
  const toggleKpis = useCallback(() => setIsKpisOpen((prev) => !prev), []);

  /**
   * Manejador para el cambio de sucursal.
   * Actualiza el estado en el componente padre y el valor en el reducer.
   * @param {any} option - La opción de sucursal seleccionada.
   */
  const handleBranchChange = (option: any) => {
    selectBranch(option);
    dispatch({ type: "SET_SELECTED_BRANCH", branch: option });
  };

  const handleCompanyChange = (option: any) => {
    selectBranch(option);
    dispatch({ type: "SET_SELECTED_COMPANY", company: option });
  };

  useEffect(() => {
    const fetchKpiData = async () => {
      if (isKpisOpen) {
        const requestPayload = {
          branch_filter: formValues?.branch_filter,
          catchment_page: formValues?.catchment_page,
        };
        try {
          const result = await trigger(requestPayload).unwrap();
          const transformStatsUI: StatItemProps[] = Object.values(result).map(
            (stat: any) => ({
              name: stat.title,
              stat: stat.data.toString(),
            })
          );
          setStatsUI(transformStatsUI);
        } catch (err) {
          console.error("Failed to fetch KPI data:", err);
        }
      }
    };

    if (isKpisOpen) fetchKpiData();
  }, [
    isKpisOpen,
    formValues?.branch_filter,
    formValues?.catchment_page,
    trigger,
  ]);

  return (
    <div className="flex flex-col lg:flex-row flex-1 self-stretch gap-y-4 lg:gap-x-2 px-4 pb-2 border-b-2">
      <NavbarFilter
        formComponents={
          <div className="flex flex-col md:flex-row flex-1 items-center gap-4 w-full">
            <InputWithCornerHint
              label="Nombre"
              span=""
              id="name"
              name="search"
              type="text"
              className="w-full"
              placeholder="Nombre"
              onChange={(e) =>
                dispatch({
                  type: "SET_FORM_VALUES",
                  values: { search: e.target.value },
                })
              }
              onBlur={() => {}}
              value={formValues?.search ?? ""}
            />
            <Select
              selected={selectedCompany}
              setSelected={handleCompanyChange}
              options={companies}
              tittle="Compañia"
              name="company_filter"
            />
            <Select
              selected={selectedBranch}
              setSelected={handleBranchChange}
              options={branches}
              tittle={t("user.views.branch")}
              name="branch_filter"
            />
          </div>
        }
        OnClickPrimaryButton={() =>
          navigate(`/branchs/${branchId}/marketing/captation/tasks`)
        }
        toggleDrawer={toggleDrawer}
        toggleKpis={toggleKpis}
        optionsMenu={optionsMenu}
        showIcons={true}
        exportData={exportData}
      />

      <Dialog
        open={isDrawerOpen}
        onClose={toggleDrawer}
        className="relative z-50"
      >
        <DialogBackdrop className="fixed inset-0 bg-black opacity-50" />

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="bg-white w-full max-w-2xl rounded-2xl shadow-xl">
            <div className="p-4 flex flex-col w-full">
              <div className="flex items-center justify-end h-7">
                <button
                  type="button"
                  onClick={toggleDrawer}
                  className="relative text-gray-400 hover:text-gray-500"
                >
                  <span className="sr-only">Close panel</span>
                  <XMarkIcon aria-hidden="true" className="h-6 w-6" />
                </button>
              </div>
              <form className="grid w-full grid-cols-1 sm:grid-cols-2 gap-4 md:flex-row md:flex-wrap md:gap-x-6">
                <Select
                  options={optionsGender}
                  selected={optionsGender.find(
                    (gender: GenderOption) =>
                      gender.id === formValues?.sex_filter
                  )}
                  setSelected={(option) =>
                    dispatch({
                      type: "SET_FORM_VALUES",
                      values: { sex_filter: option.id },
                    })
                  }
                  tittle={t("user.views.sex")}
                  name="sex_filter"
                />
                <Select
                  selected={optionsStatusBusiness.find(
                    (status) => status.id === formValues?.stages_filter
                  )}
                  setSelected={(option) =>
                    dispatch({
                      type: "SET_FORM_VALUES",
                      values: { stages_filter: option.id },
                    })
                  }
                  options={optionsStatusBusiness}
                  tittle={t("recovery.views.business_stages")}
                  name="stages_filter"
                />
                <MultiSelect
                  options={optionsTagsMarketing}
                  selectedOptions={optionsTagsMarketing?.filter((tag) =>
                    (formValues?.tag_marketing_filter || []).includes(tag.id)
                  )}
                  setSelectedOptions={(selectedOptions: any) => {
                    const selectedIds = selectedOptions.map(
                      (option: any) => option.id
                    );
                    dispatch({
                      type: "SET_FORM_VALUES",
                      values: { tag_marketing_filter: selectedIds },
                    });
                  }}
                  title={t("task.views.labels")}
                  className="w-full"
                />
                <Select
                  selected={campaigns?.find(
                    (cam: any) => cam.id === formValues?.campaign_filter
                  )}
                  setSelected={(option) =>
                    dispatch({
                      type: "SET_FORM_VALUES",
                      values: { campaign_filter: option.id },
                    })
                  }
                  options={campaigns}
                  tittle={t("recovery.views.campaign")}
                  name="campaign_filter"
                />
              </form>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      <Dialog open={isKpisOpen} onClose={toggleKpis} className="relative z-50">
        <DialogBackdrop
          className="fixed inset-0 bg-black opacity-50"
          onClick={toggleKpis}
        />
        <DialogPanel className="bg-white w-full rounded-xl lg:max-w-xl max-h-min top-48 lg:top-22 overflow-y-auto shadow-xl fixed inset-0 mx-auto overflow-hidden">
          {isLoading || isFetching ? (
            <div className="flex justify-center items-center h-20">
              <FontAwesomeIcon icon={faLoader} spin className="text-3xl" />
            </div>
          ) : (
            <StatsUI title="KPIs" stats={statsUI} onClose={toggleKpis} />
          )}
        </DialogPanel>
      </Dialog>

      <MassiveTaskDialog setOpen={setOpen} open={open} />
    </div>
  );
};

export default NavbarFilterCatchment;
